import React, { useState, useEffect } from 'react';
import api from '../api/client';
import Popup from './Popup';
import HistoryPopup from './HistoryPopup';
import Pagination from './TablePagination';
import './SecondaryTable.css';
import { usePermissions, useCurrentPageKey } from '../PermissionsContext';

const SecondaryTable = ({
  parentRowId,
  subGroup,
  endpoint,
  columns,
  fields,
  keyFieldsConfig,
  onEdit,
  enableHistory = false,
  historyConfig = {},
  queryParamsMapping = {},
  enableEdit = true,
  enableSeedAction = false,
  onSeedAction,
  seedsName,
  updateKey, // Получаем updateKey
}) => {
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupFields, setPopupFields] = useState([]);
  const [isHistoryPopupOpen, setIsHistoryPopupOpen] = React.useState(false);
  const [currentHistoryData, setCurrentHistoryData] = React.useState(null);
  const [currentRow, setCurrentRow] = React.useState(null);
  const [currentBag, setCurrentBag] = useState({});
  const { can } = usePermissions();
  const canDelete = can(useCurrentPageKey(), 'delete');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [totalItems, setTotalItems] = useState(0);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = Object.entries(queryParamsMapping).reduce((acc, [paramKey, dataKey]) => {
        const value = parentRowId[dataKey] || subGroup?.[dataKey];
        console.log(`Param ${paramKey}:`, value);
        if (value) acc[paramKey] = value;
        return acc;
      }, { page, limit });

      const response = await api.get(endpoint, { params });
      console.log('API Response:', response.data);
      if (response.data?.data) {
        setData(response.data.data);
        setTotalItems(response.data.total || 0);
      } else {
        throw new Error('Invalid response structure');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (parentRowId) fetchData();
  }, [endpoint, page, limit, parentRowId, updateKey]); // Добавляем updateKey

  const totalPages = Math.ceil(totalItems / limit);

  const handleViewDetails = (row) => {
    if (!historyConfig.endpoint || !historyConfig.columns) {
      console.error('History config is missing');
      return;
    }

    const queryParams = keyFieldsConfig
      .map(key => `${key}=${row[key]}`)
      .join('&');

    setCurrentHistoryData({
      queryParams,
      rowData: row,
    });
    setIsHistoryPopupOpen(true);
  };

  const handleCloseHistoryPopup = () => {
    setIsHistoryPopupOpen(false);
    setCurrentRow(null);
  };

  const handleEdit = (rowData) => {
    const id = keyFieldsConfig.map((key) => rowData[key]).join('-');
    onEdit(rowData, id);
  };

  const handleDelete = async (rowData) => {
    try {
      const missingKeys = keyFieldsConfig.filter(key => !rowData[key]);
      if (missingKeys.length > 0) {
        console.error('Missing keys for deletion:', missingKeys);
        return;
      }

      const queryParams = keyFieldsConfig
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(rowData[key])}`)
        .join('&');

      await api.delete(`${endpoint.split('?')[0]}?${queryParams}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting data:', error);
      alert(`Delete failed: ${error.response?.data?.message || error.response?.data?.error || error.message}`);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="secondary-table">
      <table className="secondary--table">
        <thead className="table__heading">
          <tr>
            {columns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {Array.isArray(data) && data.length > 0 ? (
            data.map((row, index) => {
              const isHighlighted = row.BagStatus === 'In Treatment' || row.BagStatus === 'Under Germination Test';
              return (
                <tr
                  key={index}
                  className={isHighlighted ? 'highlight-row' : ''}
                >
                  {columns.map((col) => (
                    <td key={col.key}>{row[col.key] ?? '-'}</td>
                  ))}
                  <td className="table__actions">
                    {enableSeedAction && (
                      <i
                        className={`fa fa-seedling ${isHighlighted ? 'highlight-icon' : ''}`}
                        onClick={() => onSeedAction({ ...row, combinedSeedsName: seedsName })}
                        style={{ cursor: 'pointer', marginRight: '10px' }}
                        title="Seed Action"
                      ></i>
                    )}
                    {enableHistory && (
                      <i
                        className="fa fa-eye"
                        title="History"
                        style={{ cursor: 'pointer', marginRight: '10px' }}
                        onClick={() => handleViewDetails(row)}
                      ></i>
                    )}
                    {enableEdit && onEdit && (
                      <i
                        className="fa fa-edit"
                        title="Edit"
                        style={{ cursor: 'pointer', marginRight: '10px' }}
                        onClick={() => handleEdit(row)}
                      ></i>
                    )}
                    {canDelete ? (
                      <i
                        className="fa fa-trash"
                        title="Delete"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleDelete(row)}
                      ></i>
                    ) : (
                      <i
                        className="fa fa-trash"
                        title="Delete"
                        style={{ cursor: 'not-allowed', opacity: 0.5 }}
                      ></i>
                    )}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={columns.length + 1} style={{ textAlign: 'center' }}>
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <Pagination
        page={page}
        totalPages={totalPages}
        limit={limit}
        setPage={setPage}
        setLimit={setLimit}
      />
      {isHistoryPopupOpen && currentHistoryData && (
        <HistoryPopup
          isOpen={isHistoryPopupOpen}
          endpoint={historyConfig.endpoint}
          queryParams={currentHistoryData.queryParams}
          columns={historyConfig.columns}
          onClose={handleCloseHistoryPopup}
        />
      )}
    </div>
  );
};

export default SecondaryTable;